/**
 * AdminSidebar Component
 *
 * Navigation sidebar for admin section with:
 * - Capability-gated navigation items
 * - Active state indication
 * - Collapsible groups
 */

import { Building2, Users, ClipboardList, Activity, ChevronLeft, Shield } from "lucide-react";
import type { ReactElement } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { useAuthStore } from "../../store/authStore";
import type { Capability } from "../../types";

interface NavItem {
  readonly path: string;
  readonly label: string;
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly capability: Capability;
}

const NAV_ITEMS: readonly NavItem[] = [
  {
    path: "/admin/tenants",
    label: "Tenants",
    icon: Building2,
    capability: "admin:tenants",
  },
  {
    path: "/admin/users",
    label: "Users",
    icon: Users,
    capability: "admin:users",
  },
  {
    path: "/admin/audit",
    label: "Audit Logs",
    icon: ClipboardList,
    capability: "admin:audit",
  },
  {
    path: "/admin/health",
    label: "System Health",
    icon: Activity,
    capability: "admin:health",
  },
];

export function AdminSidebar(): ReactElement {
  const navigate = useNavigate();
  const { hasCapability, hasAnyCapability } = useAuthStore();

  // Filter nav items based on user capabilities
  const visibleNavItems = NAV_ITEMS.filter((item) => hasCapability(item.capability));

  // Check if user has any admin capability
  const hasAdminAccess = hasAnyCapability([
    "admin:tenants",
    "admin:users",
    "admin:audit",
    "admin:health",
  ]);

  return (
    <aside className="flex w-64 flex-col border-r border-border bg-background-secondary">
      {/* Header */}
      <div className="flex h-14 items-center justify-between border-b border-border px-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <span className="font-semibold text-foreground">Admin</span>
        </div>
        <button
          onClick={() => navigate("/")}
          className="rounded p-1 text-foreground-muted hover:bg-background hover:text-foreground"
          title="Back to Editor"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3">
        {hasAdminAccess ? (
          <ul className="space-y-1">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-foreground-muted hover:bg-background hover:text-foreground"
                      }`
                    }
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center text-foreground-muted">
            <Shield className="h-8 w-8 mb-2" />
            <p className="text-sm">No admin access</p>
            <p className="text-xs mt-1">Contact your administrator</p>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-3">
        <button
          onClick={() => navigate("/")}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground-muted hover:bg-background-secondary hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Studio</span>
        </button>
      </div>
    </aside>
  );
}

export default AdminSidebar;
